import {
	UserRoleLabels,
	UserRoles,
	getUserClients,
	getUserCompanies,
} from "@/api/user.service";
import { formatPhoneBR } from "@/utils/phoneUtils";
import {
	Autocomplete,
	Box,
	Checkbox,
	Divider,
	FormControlLabel,
	IconButton,
	InputAdornment,
	Stack,
	Tab,
	Tabs,
	TextField,
	Typography,
} from "@mui/material";
import { getCompanies, type Company } from "@/api/company.service";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNotification } from "@/context/NotificationContext";
import {
	getClients,
	getModulesByClient,
	type Client,
} from "@/api/client.service";
import {
	getModules,
	type GenericModule,
	type Module,
} from "@/api/module.service";
import CompanyModuleSection from "@/components/user/CompanyModuleSection";
import CompanyTransferList from "@/components/company/CompanyTransferList";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import GlobalModuleActions from "@/components/user/GlobalModuleActions";
import { getModuleRoles, MODULE_ROLES } from "@/api/moduleRole.service";
import ApiTokensSection from "@/components/user/ApiTokensSection";

interface UserFormProps {
	values: any;
	setValues: any;
	isEditing: any;
	// Flag como está salvo: a aba de tokens segue o servidor, não o checkbox
	// ainda não salvo (a API recusa tokens para quem não é conta de serviço).
	savedServiceAccount?: boolean;
}

async function fetchAllCompanies(clientId: string): Promise<Company[]> {
	const pageSize = 200;
	const first = await getCompanies({ clientId, limit: pageSize, offset: 0 });
	const total = first.total ?? 0;
	if (total <= pageSize) return first.companies ?? [];
	const pages = await Promise.all(
		Array.from(
			{ length: Math.ceil((total - pageSize) / pageSize) },
			(_, i) =>
				getCompanies({
					clientId,
					limit: pageSize,
					offset: (i + 1) * pageSize,
				}),
		),
	);
	return [
		...(first.companies ?? []),
		...pages.flatMap((p) => p.companies ?? []),
	];
}

function onlyDigits(value: string) {
	return value.replace(/\D/g, "");
}

export function UserForm({
	values,
	setValues,
	isEditing,
	savedServiceAccount = false,
}: UserFormProps) {
	const { user } = useAuth();
	const { notify } = useNotification();
	const [tabValue, setTabValue] = useState(0);
	const [errors, setErrors] = useState<Record<string, string>>({});

	// Field-specific validators (called on blur)
	const validateName = () => {
		if (!values.name?.trim()) {
			setErrors((prev) => ({ ...prev, name: "Nome é obrigatório" }));
		} else {
			setErrors((prev) => ({ ...prev, name: "" }));
		}
	};

	const validateEmail = () => {
		if (!values.email?.trim()) {
			setErrors((prev) => ({ ...prev, email: "Email é obrigatório" }));
		} else {
			setErrors((prev) => ({ ...prev, email: "" }));
		}
	};

	const validateRole = () => {
		if (!values.role) {
			setErrors((prev) => ({ ...prev, role: "Cargo é obrigatório" }));
		} else {
			setErrors((prev) => ({ ...prev, role: "" }));
		}
	};

	const validatePhone = () => {
		const digits = onlyDigits(values.phone ?? "");
		const local = digits.startsWith("55") ? digits.slice(2) : digits;
		if (!local && values.is_service_account) {
			setErrors((prev) => ({ ...prev, phone: "" }));
		} else if (!local) {
			setErrors((prev) => ({ ...prev, phone: "Telefone é obrigatório" }));
		} else if (local.length !== 10 && local.length !== 11) {
			setErrors((prev) => ({
				...prev,
				phone: "Telefone inválido. Use (DD) XXXX-XXXX ou (DD) XXXXX-XXXX",
			}));
		} else {
			setErrors((prev) => ({ ...prev, phone: "" }));
		}
	};

	const validatePassword = () => {
		if (
			!isEditing &&
			!values.is_service_account &&
			!values.password?.trim()
		) {
			setErrors((prev) => ({
				...prev,
				password: "Senha é obrigatória para novo usuário",
			}));
		} else {
			setErrors((prev) => ({ ...prev, password: "" }));
		}
	};

	const [clients, setClients] = useState<Client[]>([]);
	const [selectedClient, setSelectedClient] = useState<Client | null>(null);

	// Companies the logged-in user is allowed to see at all
	const [loggedCompanies, setLoggedCompanies] = useState<Company[]>([]);

	const [allModules, setAllModules] = useState<Module[]>([]);
	const [clientModules, setClientModules] = useState<GenericModule[]>([]);
	const [showPassword, setShowPassword] = useState(false);

	const isAdmin = user?.role === UserRoles.Admin;
	const showTokensTab =
		isAdmin &&
		isEditing &&
		savedServiceAccount &&
		!!values.is_service_account &&
		!!values.id;

	// Derived: filter loggedCompanies by the selected client (if any)
	const usableCompanies = selectedClient
		? loggedCompanies.filter((c) => c.client_id === selectedClient.id)
		: loggedCompanies;

	const selectedCompaniesData = usableCompanies.filter((comp) =>
		values.companies?.includes(comp.id),
	);

	// --- Handlers ---

	const handleClickShowPassword = () => setShowPassword((prev) => !prev);
	const handleMouseDownPassword = (event: React.MouseEvent) =>
		event.preventDefault();
	const handleTabChange = (_: React.SyntheticEvent, newValue: number) =>
		setTabValue(newValue);

	const handleChangeSelectedClient = async (newValue: Client | null) => {
		setSelectedClient(newValue);

		if (newValue) {
			const newModules = await getModulesByClient(newValue.id);
			setClientModules(
				newModules.map((m) => ({
					id: m.module_id,
					label: m.module_id,
				})),
			);

			if (isAdmin) {
				const companies = await fetchAllCompanies(newValue.id);
				setLoggedCompanies(companies);
			}
		} else {
			setClientModules(
				allModules.map((m) => ({ id: m.id, label: m.label })),
			);
			if (isAdmin) setLoggedCompanies([]);
		}
	};

	// Role options
	const options = Object.values(UserRoles)
		.filter((role) => isAdmin || role !== UserRoles.Admin)
		.map((role) => ({ id: role, label: UserRoleLabels[role] }));

	useEffect(() => {
		if (!user) return;

		const resolveLoggedCompanies = async () => {
			if (isAdmin) {
				const fetched = await getClients({ limit: 1000, offset: 0 });
				setClients(fetched.clients);
			} else {
				const [userCompanies, userClients] = await Promise.all([
					getUserCompanies(user.id),
					getUserClients(user.id),
				]);
				setLoggedCompanies(userCompanies ?? []);
				const clientList = userClients ?? [];
				setClients(clientList);

				const initialClient =
					user.loggedClient ??
					(clientList.length === 1 ? clientList[0] : null) ??
					null;
				setSelectedClient(initialClient);

				// Se não há loggedClient mas há um cliente auto-selecionado, carrega os módulos
				if (initialClient && !user.loggedClient) {
					const newModules = await getModulesByClient(
						initialClient.id,
					);
					setClientModules(
						newModules.map((m) => ({
							id: m.module_id,
							label: m.module_id,
						})),
					);
				}
			}
		};

		resolveLoggedCompanies();
	}, [user]);

	useEffect(() => {
		if (!isEditing || !values.id) return;

		const fillCompanyValues = async () => {
			try {
				const data = await getUserCompanies(values.id);
				setValues((prev: any) => ({
					...prev,
					companies: data ? data.map((c) => c.id) : [],
				}));
			} catch (error) {
				console.error("Failed to fetch companies:", error);
			}
		};

		fillCompanyValues();
	}, [values.id, isEditing]);

	// Módulos que o usuário já tem em alguma empresa do cliente selecionado e
	// que, além disso, definem cargo(s) próprio(s) em MODULE_ROLES.
	const assignedModuleIds = new Set<string>();
	selectedCompaniesData.forEach((company) => {
		(values.companyModules?.[company.id] ?? []).forEach(
			(moduleId: string) => assignedModuleIds.add(moduleId),
		);
	});
	const moduleIdsWithRoles = Object.keys(MODULE_ROLES).filter((id) =>
		assignedModuleIds.has(id),
	);
	const moduleIdsWithRolesKey = moduleIdsWithRoles.slice().sort().join(",");

	// Os cargos de módulo são escopados por cliente, então acompanham o
	// cliente selecionado e só existem para módulos que o usuário já tem.
	useEffect(() => {
		if (
			!isAdmin ||
			!isEditing ||
			!values.id ||
			!selectedClient?.id ||
			!moduleIdsWithRolesKey
		)
			return;

		let cancelled = false;
		const moduleIds = moduleIdsWithRolesKey.split(",");

		Promise.all(
			moduleIds.map((moduleId) =>
				getModuleRoles(values.id, selectedClient.id, moduleId).then(
					(res) => [moduleId, res.roles ?? []] as const,
				),
			),
		)
			.then((results) => {
				if (cancelled) return;
				const loaded: Record<string, string[]> = {};
				results.forEach(([moduleId, roles]) => {
					loaded[moduleId] = roles;
				});
				setValues((prev: any) => ({
					...prev,
					moduleRoles: { ...(prev.moduleRoles ?? {}), ...loaded },
					moduleRolesInitial: {
						...(prev.moduleRolesInitial ?? {}),
						...loaded,
					},
					moduleRolesClientId: selectedClient.id,
				}));
			})
			.catch((error) => {
				console.error("Failed to fetch module roles:", error);
			});

		return () => {
			cancelled = true;
		};
	}, [
		isAdmin,
		isEditing,
		values.id,
		selectedClient?.id,
		moduleIdsWithRolesKey,
	]);

	useEffect(() => {
		if (!user) return;

		const fillModules = async () => {
			try {
				const manifestModules = await getModules();
				setAllModules(manifestModules);

				if (isAdmin) {
					setClientModules(
						manifestModules.map((m) => ({
							id: m.id,
							label: m.label,
						})),
					);
					return;
				}

				if (!user.loggedClient?.id) return;

				const newModules = await getModulesByClient(
					user.loggedClient.id,
				);
				setClientModules(
					newModules.map((m) => ({
						id: m.module_id,
						label: m.module_id,
					})),
				);
			} catch (e) {
				notify(
					"Erro ao preencher módulos disponíveis. Tente novamente mais tarde",
					"error",
				);
				console.error("Error trying to fill modules:", e);
			}
		};

		fillModules();
	}, [user]);

	const handleModulesChange = (companyId: string, moduleIds: string[]) => {
		setValues((prev: any) => ({
			...prev,
			companyModules: {
				...(prev.companyModules ?? {}),
				[companyId]: moduleIds,
			},
		}));
	};

	return (
		<Box sx={{ width: "100%" }}>
			<Tabs
				value={tabValue}
				onChange={(event, newValue) => handleTabChange(event, newValue)}
				sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
			>
				<Tab label="Dados Básicos" />
				<Tab label="Empresas e Módulos" />
				{showTokensTab && <Tab label="Tokens de API" />}
			</Tabs>

			{/* Basic data */}
			{tabValue === 0 && (
				<Stack spacing={3}>
					<FormControlLabel
						control={
							<Checkbox
								checked={!isEditing || !!values.active}
								onChange={(e) =>
									setValues({
										...values,
										active: e.target.checked,
									})
								}
								disabled={!isEditing}
							/>
						}
						label="Ativo"
					/>

					{isAdmin && (
						<FormControlLabel
							control={
								<Checkbox
									checked={!!values.is_service_account}
									onChange={(e) =>
										setValues({
											...values,
											is_service_account:
												e.target.checked,
										})
									}
								/>
							}
							label="Conta de serviço (integração via token de API, sem acesso à interface)"
						/>
					)}

					<TextField
						label="Nome"
						value={values.name || ""}
						onChange={(e) =>
							setValues({ ...values, name: e.target.value })
						}
						onBlur={validateName}
						error={!!errors.name}
						helperText={errors.name}
						required
						fullWidth
					/>

					<TextField
						label="Email"
						value={values.email || ""}
						onChange={(e) =>
							setValues({ ...values, email: e.target.value })
						}
						onBlur={validateEmail}
						error={!!errors.email}
						helperText={errors.email}
						autoComplete="new-email"
						required
						fullWidth
					/>

					<Autocomplete
						disablePortal
						options={options}
						getOptionLabel={(option) => option.label}
						value={
							options.find((o) => o.id === values.role) || null
						}
						onChange={(_, newValue) =>
							setValues({ ...values, role: newValue?.id ?? null })
						}
						onBlur={validateRole}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Cargo"
								required
								error={!!errors.role}
								helperText={errors.role}
							/>
						)}
						fullWidth
					/>

					<TextField
						label="Telefone"
						value={formatPhoneBR(values.phone || "")}
						onChange={(e) => {
							const digits = onlyDigits(e.target.value);
							setValues({ ...values, phone: digits });
						}}
						onBlur={validatePhone}
						error={!!errors.phone}
						helperText={errors.phone}
						placeholder="+55 (11) 99999-9999"
						required={!values.is_service_account}
						fullWidth
					/>

					<TextField
						label="Código ERP"
						value={values.codigo_erp || ""}
						onChange={(e) =>
							setValues({ ...values, codigo_erp: e.target.value })
						}
						fullWidth
					/>

					{!values.is_service_account && (
						<TextField
							label="Senha"
							type={showPassword ? "text" : "password"}
							value={values.password || ""}
							required={!isEditing}
							onChange={(e) =>
								setValues({
									...values,
									password: e.target.value,
								})
							}
							onBlur={validatePassword}
							error={!!errors.password}
							helperText={errors.password}
							autoComplete="new-password"
							slotProps={{
								input: {
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												aria-label="toggle password visibility"
												onClick={
													handleClickShowPassword
												}
												onMouseDown={
													handleMouseDownPassword
												}
												edge="end"
											>
												{showPassword ? (
													<VisibilityOff />
												) : (
													<Visibility />
												)}
											</IconButton>
										</InputAdornment>
									),
								},
							}}
							fullWidth
						/>
					)}
				</Stack>
			)}
			{/* Companies and Modules */}
			{tabValue === 1 && (
				<Box sx={{ marginBottom: 5 }}>
					{user && (isAdmin || clients.length >= 1) && (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 2,
								p: 2,
								borderRadius: 2,
								bgcolor: "action.hover",
							}}
						>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ flexShrink: 0 }}
							>
								Filtrar por cliente:
							</Typography>

							<Autocomplete
								options={clients}
								value={selectedClient}
								fullWidth
								onChange={(_, newValue) =>
									handleChangeSelectedClient(newValue)
								}
								getOptionLabel={(option) =>
									option.name || "Cliente sem nome"
								}
								isOptionEqualToValue={(option, value) =>
									option.id === value.id
								}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Cliente"
										size="small"
									/>
								)}
								sx={{ minWidth: 260 }}
							/>
						</Box>
					)}

					{selectedClient && (
						<>
							<Typography
								variant="subtitle2"
								gutterBottom
								color="text.secondary"
								sx={{ mt: 2 }}
							>
								Empresas do usuário
							</Typography>

							<CompanyTransferList
								usableCompanies={usableCompanies}
								values={values}
								setValues={setValues}
							/>

							<Divider
								orientation="horizontal"
								variant="middle"
								flexItem
								sx={{ height: 30 }}
							/>

							{selectedCompaniesData.length === 0 ? (
								<Typography
									variant="body2"
									sx={{
										textAlign: "center",
										py: 4,
										color: "text.disabled",
									}}
								>
									Nenhuma empresa selecionada. Selecione uma
									empresa acima para configurar os módulos.
								</Typography>
							) : clientModules.length === 0 ? (
								<Typography
									variant="body2"
									sx={{
										textAlign: "center",
										py: 4,
										color: "text.disabled",
									}}
								>
									Nenhum módulo disponível para este cliente.
									Favor, verifique a situação do cadastro
									deste com o suporte.
								</Typography>
							) : (
								<>
									<Typography
										variant="subtitle2"
										gutterBottom
										color="text.secondary"
										sx={{ mt: 2 }}
									>
										Módulos do usuário por empresa
									</Typography>

									<GlobalModuleActions
										selectedCompaniesData={
											selectedCompaniesData
										}
										clientModules={clientModules}
										onModulesChange={handleModulesChange}
									/>

									{selectedCompaniesData.map((company) => (
										<CompanyModuleSection
											key={company.id}
											company={company}
											userId={values.id}
											clientModules={clientModules}
											allModules={allModules}
											value={
												values.companyModules?.[
													company.id
												]
											}
											onChange={(moduleIds) =>
												handleModulesChange(
													company.id,
													moduleIds,
												)
											}
										/>
									))}

									{isAdmin &&
										moduleIdsWithRoles.length > 0 && (
											<Box sx={{ mt: 3 }}>
												<Typography
													variant="subtitle2"
													gutterBottom
													color="text.secondary"
												>
													Cargos do módulo
												</Typography>

												<Stack spacing={1}>
													{moduleIdsWithRoles.map(
														(moduleId) => (
															<Stack
																key={moduleId}
																direction="row"
																spacing={2}
																alignItems="center"
																flexWrap="wrap"
															>
																{MODULE_ROLES[
																	moduleId
																]?.map(
																	(
																		roleDef,
																	) => (
																		<FormControlLabel
																			key={
																				roleDef.role
																			}
																			control={
																				<Checkbox
																					checked={(
																						values
																							.moduleRoles?.[
																							moduleId
																						] ??
																						[]
																					).includes(
																						roleDef.role,
																					)}
																					onChange={(
																						e,
																					) => {
																						const current =
																							new Set<string>(
																								values
																									.moduleRoles?.[
																									moduleId
																								] ??
																									[],
																							);
																						if (
																							e
																								.target
																								.checked
																						)
																							current.add(
																								roleDef.role,
																							);
																						else
																							current.delete(
																								roleDef.role,
																							);
																						setValues(
																							{
																								...values,
																								moduleRoles:
																									{
																										...(values.moduleRoles ??
																											{}),
																										[moduleId]:
																											Array.from(
																												current,
																											),
																									},
																								moduleRolesClientId:
																									selectedClient?.id ??
																									null,
																							},
																						);
																					}}
																				/>
																			}
																			label={
																				roleDef.label
																			}
																		/>
																	),
																)}
															</Stack>
														),
													)}
												</Stack>
											</Box>
										)}
								</>
							)}
						</>
					)}
				</Box>
			)}
			{/* API tokens (service accounts only) */}
			{tabValue === 2 && showTokensTab && (
				<ApiTokensSection userId={values.id} />
			)}
		</Box>
	);
}
